import type { ThemeConfig } from "antd";

/** Palette del mockup v2 (variabili CSS --primary, --nav, --ok, ...). */
export const palette = {
  bg: "#F8F5EF",
  card: "#FFFFFF",
  sunk: "#F3EFE7",
  hair: "#E2DED3",
  fg: "#16202B",
  heading: "#141F2C",
  muted: "#5C6670",
  primary: "#1A4470",
  primarySoft: "#E3ECF5",
  nav: "#0B1B2E",
  navFg: "#C9D3DD",
  ok: "#2E6B4F",
  okSoft: "#DCE8E2",
  warn: "#9A6200",
  warnSoft: "#F6E9CF",
  bad: "#B3261E",
  badSoft: "#F7DEDB",
  crit: "#6E1A3A",
  critSoft: "#EED8E0",
  secS: "#1A4470",
  secH: "#7A5C1E",
  secE: "#2E6B4F",
  secX: "#7E95A1",
};

export const fonts = {
  sans: '"Manrope Variable", Manrope, system-ui, -apple-system, "Segoe UI", sans-serif',
  mono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
};

export const theme: ThemeConfig = {
  token: {
    colorPrimary: palette.primary,
    colorSuccess: palette.ok,
    colorWarning: palette.warn,
    colorError: palette.bad,
    colorInfo: palette.primary,
    colorBgLayout: palette.bg,
    colorText: palette.fg,
    colorBorderSecondary: palette.hair,
    borderRadius: 8,
    fontFamily: fonts.sans,
    fontFamilyCode: fonts.mono,
  },
  components: {
    Layout: { siderBg: palette.nav, triggerBg: palette.nav, headerBg: palette.card, bodyBg: palette.bg },
    // fondi chiari espliciti: quelli derivati dal colore primario scuro uscivano grigi
    Alert: { colorInfoBg: palette.primarySoft, colorInfoBorder: "#C7D6E8", colorWarningBg: palette.warnSoft, colorWarningBorder: "#E6CF9F" },
    Menu: { darkItemBg: palette.nav, darkSubMenuItemBg: palette.nav, darkItemColor: palette.navFg, darkItemSelectedBg: palette.primary },
  },
};

/** CSS globali minimi. */
export const globalCss = `
html,body{margin:0;padding:0;background:${palette.bg};font-family:${fonts.sans};color:${palette.fg};-webkit-text-size-adjust:100%}
code,kbd,pre,.mono,.num{font-family:${fonts.mono}}
.num{font-variant-numeric:tabular-nums}
:root{--hk-pine:${palette.primary};--hk-pine-soft:${palette.primarySoft};--hk-ink:${palette.fg};--hk-ink-soft:${palette.muted};--hk-paper:${palette.bg};--hk-paper-2:${palette.sunk};--hk-line:${palette.hair};--hk-amber:${palette.warn};--hk-amber-soft:${palette.warnSoft};--hk-z-guide:1000;--hk-z-onboarding:1100}
.sr-only{position:absolute!important;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
#main:focus{outline:none}
.aiuto-figura{font-size:64px;color:${palette.primary};display:flex;align-items:center;justify-content:center;height:100%}
.ant-menu-dark .ant-menu-item:focus,.ant-menu-dark .ant-menu-item:focus-visible{outline:none!important;box-shadow:none!important}
.rs-kpi{display:grid;gap:12px;grid-template-columns:repeat(5,minmax(0,1fr))}
@media (max-width:991px){.rs-kpi{grid-template-columns:repeat(2,minmax(0,1fr))}}
.rs-grid .ant-table-cell{vertical-align:top}
.rs-grid .ant-table-tbody>tr.rs-first>td{border-top:1px solid ${palette.hair}}
.rs-grid .ant-input,.rs-grid .ant-select-selector,.rs-grid .ant-input-number{border-color:transparent!important;background:transparent!important;box-shadow:none!important}
.rs-grid .ant-input:hover,.rs-grid .ant-select:hover .ant-select-selector,.rs-grid .ant-input-number:hover{border-color:${palette.hair}!important;background:#fff!important}
.rs-grid .ant-input:focus,.rs-grid .ant-input-focused,.rs-grid .ant-select-focused .ant-select-selector,.rs-grid .ant-input-number-focused{border-color:${palette.primary}!important;background:#fff!important}
.rs-grid .ant-table-tbody>tr>td{padding:4px 6px!important}
.rs-grid .ant-input.ant-input-status-error{border-color:${palette.bad}!important;background:${palette.badSoft}!important}
.rs-savebar{position:sticky;bottom:0;z-index:10;background:${palette.card};border-top:1px solid ${palette.hair};padding:10px 16px;display:flex;gap:12px;align-items:center;justify-content:flex-end;box-shadow:0 -4px 12px rgba(20,31,44,.06)}
.guida .ant-collapse-content-box{padding-top:0}
.guida-testo{font-size:14px;line-height:1.6}
.guida-testo h2{font-size:15px;margin:18px 0 6px;color:${palette.primary}}
.guida-testo p,.guida-testo ul,.guida-testo ol{margin:0 0 10px}
.guida-testo ul,.guida-testo ol{padding-left:20px}
.guida-testo table{border-collapse:collapse;width:100%;font-size:13px;margin:8px 0}
.guida-testo th,.guida-testo td{border:1px solid ${palette.hair};padding:4px 6px;text-align:left}
.guida-testo code{background:${palette.sunk};padding:0 4px;border-radius:3px;font-size:12px}

/* Portale Qualità (mockup v2): severità, flag, proposte, barra del processo, KPI, tessere, classifiche */
.sev{display:inline-block;border-radius:4px;padding:0 7px;font-size:11.5px;line-height:20px;font-weight:700;letter-spacing:.3px;font-family:${fonts.mono}}
.sev.CRITICAL{background:${palette.critSoft};color:${palette.crit}}.sev.HIGH{background:${palette.badSoft};color:${palette.bad}}
.sev.MEDIUM{background:${palette.warnSoft};color:${palette.warn}}.sev.LOW{background:${palette.sunk};color:${palette.muted}}
.urg{color:${palette.bad};font-weight:700;font-size:11px;margin-left:4px}
.flag-new,.flag-upd{color:#fff;border-radius:3px;font-size:10.5px;padding:0 5px;margin-left:6px;font-weight:700;letter-spacing:.3px}
.flag-new{background:${palette.primary}}.flag-upd{background:${palette.secH}}
.prop{border:1px dashed ${palette.warn};background:${palette.warnSoft};color:${palette.warn};border-radius:4px;font-size:11px;padding:0 5px;margin-left:4px;font-weight:500;white-space:nowrap}
.ro{border:1px solid ${palette.hair};background:${palette.sunk};color:${palette.muted};border-radius:4px;font-size:11px;padding:0 5px;margin-left:4px;font-weight:500;white-space:nowrap}
.miss{color:${palette.bad};font-style:italic}
.muted{color:${palette.muted}}
.small{font-size:12px}
.kv{display:grid;grid-template-columns:150px 1fr;gap:6px 12px;font-size:13px;margin:0}
.kv dt{color:${palette.muted}}.kv dd{margin:0;min-width:0}
.kv.stretto{grid-template-columns:120px 1fr}
.flowline{display:flex;align-items:stretch;margin:6px 0 16px;flex-wrap:wrap}
.flowline .st{flex:1;min-width:110px;padding:8px 10px;background:${palette.sunk};border:1px solid ${palette.hair};margin-right:-1px;font-size:12.5px}
.flowline .st b{display:block;font-size:12px}
.flowline .st.done{background:${palette.okSoft};border-color:#BFD5C9}
.flowline .st.cur{background:${palette.primarySoft};border-color:#C7D6E8;box-shadow:inset 0 -3px 0 ${palette.primary}}
.flowline .st:first-child{border-radius:8px 0 0 8px}.flowline .st:last-child{border-radius:0 8px 8px 0}
.kpi-l{font-size:12px;color:${palette.muted}}.kpi-v{font-size:24px;font-weight:700;font-family:${fonts.mono};margin-top:2px;line-height:1.3}
.kpi-d{font-size:12px;color:${palette.muted};margin-top:2px}
.hbar{display:flex;height:22px;border-radius:5px;overflow:hidden;background:${palette.sunk};font-size:11px;color:#fff;font-family:${fonts.mono}}
.hbar i{display:flex;align-items:center;justify-content:center;font-style:normal;min-width:18px}
.legend{display:flex;gap:14px;font-size:12px;color:${palette.muted};flex-wrap:wrap}
.legend i{display:inline-block;width:10px;height:10px;border-radius:2px;margin-right:4px;vertical-align:-1px}
.rank{display:grid;gap:8px}
.rank .r{display:grid;grid-template-columns:160px 1fr 120px;gap:10px;align-items:center;font-size:12.5px}
.rank .bar{height:10px;background:${palette.sunk};border-radius:4px;overflow:hidden}.rank .bar i{display:block;height:100%}
.chart{display:flex;align-items:flex-end;gap:6px;height:120px;padding:8px 0}
.chart{justify-content:center}.chart i{flex:1;max-width:56px;background:${palette.primary};border-radius:3px 3px 0 0;opacity:.85;min-height:2px}
.tiles{display:grid;gap:14px;grid-template-columns:repeat(auto-fill,minmax(280px,1fr))}
.tile{background:${palette.card};border:1px solid ${palette.hair};border-radius:12px;padding:16px;text-decoration:none;color:inherit;display:flex;flex-direction:column;gap:6px;position:relative}
a.tile:hover{border-color:${palette.primary};box-shadow:0 4px 14px rgba(20,31,44,.08)}
.tile .ti{width:38px;height:38px;border-radius:10px;display:grid;place-items:center;color:#fff;font-weight:800;font-size:14px}
.tile .tb{position:absolute;top:14px;right:14px}
.tile small{color:${palette.muted}}
.tile.off{opacity:.55;cursor:default}
.mail{background:#fff;border:1px solid ${palette.hair};border-radius:8px;padding:20px 24px;font-size:13.5px;line-height:1.6;max-width:860px;white-space:pre-wrap;font-family:${fonts.sans};margin:0}
.d8 .ant-card-head-title{display:flex;gap:8px;align-items:center}
.d8n{font-family:${fonts.mono};background:${palette.primary};color:#fff;border-radius:4px;padding:0 6px;font-size:12px}
.prev{border:1px dashed ${palette.hair};border-radius:8px;background:${palette.sunk};min-height:80px;display:grid;place-items:center;color:${palette.muted};font-size:12.5px;padding:12px;text-align:center}
.crumbs{font-size:12.5px;color:${palette.muted};margin-bottom:12px}
.crumbs a{color:${palette.muted}}
.pagehead{display:flex;flex-wrap:wrap;justify-content:space-between;align-items:flex-end;gap:12px;margin-bottom:18px}
.pagehead h1{font-size:21px;margin:0;color:${palette.heading};font-weight:700;letter-spacing:-.01em}
.pagehead p{margin:4px 0 0;color:${palette.muted}}
.ant-table-cell .mono a,.mono a{font-family:${fonts.mono}}
.rank .r .num{white-space:nowrap}
@media (max-width:991px){.rank .r{grid-template-columns:minmax(0,120px) 1fr max-content}.kv{grid-template-columns:110px 1fr}}
`;
